import { create } from "zustand";

type StepsWaiting = {
  [stepId: number]: Set<number>; // Set of fieldIds waiting per step
};

type WorkflowIntegrationState = {
  [workflowId: number]: StepsWaiting;
};

export type IntegrationPayload = {
  workflowId: number;
  stepId: number;
  fieldId: number;
};

type IntegrationTrackingState = {
  workflows: WorkflowIntegrationState;
  setStepIntegrationPending: (payload: IntegrationPayload) => void;
  setStepIntegrationFinished: (payload: IntegrationPayload) => void;
  getStepsWaiting: (workflowId: number) => StepsWaiting;
};

export const useIntegrationTrackingStore = create<IntegrationTrackingState>(
  (set, get) => ({
    workflows: {},

    setStepIntegrationPending: ({ workflowId, stepId, fieldId }) => {
      set(state => {
        const workflowData = state.workflows[workflowId] || {};
        const stepsWaiting = { ...workflowData };

        // Add the pending field's ID into stepID set
        stepsWaiting[stepId] = new Set(stepsWaiting[stepId] || []);
        stepsWaiting[stepId].add(fieldId);

        return {
          workflows: {
            ...state.workflows,
            [workflowId]: stepsWaiting
          }
        };
      });
    },

    setStepIntegrationFinished: ({ workflowId, stepId, fieldId }) => {
      set(state => {
        const workflowData = state.workflows[workflowId] || {};
        const stepsWaiting = { ...workflowData };

        // Remove the field from stepID's set if it exists
        if (stepsWaiting[stepId]) {
          stepsWaiting[stepId].delete(fieldId);

          // If there are no other fields left in this step, remove the entire step
          if (stepsWaiting[stepId].size === 0) {
            delete stepsWaiting[stepId];
          }
        }

        return {
          workflows: {
            ...state.workflows,
            [workflowId]: stepsWaiting
          }
        };
      });
    },

    getStepsWaiting: workflowId => {
      return get().workflows[workflowId] || {};
    }
  })
);
